import { useState, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { orderBy } from "firebase/firestore";
import { useFirestoreCollection } from "../lib/useFirestoreCollection";
import ServiceMechanical from "../components/svg/ServiceMechanical.jsx";
import ServiceImport from "../components/svg/ServiceImport.jsx";
import ServiceElectronicIoT from "../components/svg/ServiceElectronicIoT.jsx";
import ServiceAutomation from "../components/svg/ServiceAutomation.jsx";
import ServiceDigitalTwin from "../components/svg/ServiceDigitalTwin.jsx";
import ServiceRetrofit from "../components/svg/ServiceRetrofit.jsx";
import Service3DPrint from "../components/svg/Service3DPrint.jsx";
import ServiceLabFab from "../components/svg/ServiceLabFab.jsx";
import styles from "./Services.module.css";

const SERVICES = [
  {
    Icon: ServiceMechanical,
    code: "01",
    title: "Diseño mecánico & fabricación",
    body: "Equipos para investigación y laboratorio diseñados y fabricados a medida.",
  },
  {
    Icon: ServiceImport,
    code: "02",
    title: "Importación de equipo de laboratorio",
    body: "Selección, importación y comisionamiento de instrumentación científica.",
  },
  {
    Icon: ServiceElectronicIoT,
    code: "03",
    title: "Electrónica, IoT & adquisición de datos",
    body: "Tarjetas, redes y dispositivos conectados, con lectura de sensores, telemetría y persistencia de datos.",
  },
  {
    Icon: ServiceAutomation,
    code: "04",
    title: "Automatización de procesos",
    body: "PLC, HMI y lógica de control para producción continua y por lotes.",
  },
  {
    Icon: ServiceDigitalTwin,
    code: "05",
    title: "Gemelos digitales",
    body: "Réplicas virtuales sincronizadas con el equipo físico para simular y predecir.",
  },
  {
    Icon: ServiceRetrofit,
    code: "06",
    title: "Retrofit & modernización",
    body: "Actualización de maquinaria existente con electrónica y control modernos.",
  },
  {
    Icon: Service3DPrint,
    code: "07",
    title: "Diseño orientado a impresión 3D",
    body: "Modelado y optimización de piezas para manufactura aditiva e impresión 3D.",
  },
  {
    Icon: ServiceLabFab,
    code: "08",
    title: "Fabricación & equipamiento de pruebas",
    body: "Componentes a medida, bancos de ensayo y equipamiento para pruebas de laboratorio.",
  },
];

function useIsMobile(maxWidth = 760) {
  const query = `(max-width:${maxWidth}px)`;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return isMobile;
}

export default function Services() {
  // contenido editable de detalle por servicio (colección "services", id = code)
  const { items: detailDocs } = useFirestoreCollection("services");
  const detailMap = Object.fromEntries(detailDocs.map((d) => [d.id, d]));

  const isMobile = useIsMobile();
  const [selected, setSelected] = useState(null); // code del servicio abierto
  const open = selected ? SERVICES.find((s) => s.code === selected) : null;

  return (
    <section id="servicios" className={styles.wrap} data-open={!!open}>
      <header className={styles.head}>
        <span className="mono-tag">// 03 — Qué hacemos</span>
        <h2 className={styles.h2}>
          Ocho frentes técnicos,
          <br />
          <span className={styles.h2alt}>una sola disciplina:</span>
          <span className={styles.tail}> resolver lo físico.</span>
        </h2>
      </header>

      <div className={styles.layout} data-open={!!open}>
        <ul className={styles.grid} data-split={!!open}>
          {SERVICES.map((s) => {
            const isActive = s.code === selected;
            const collapsed = isMobile && isActive;
            const customTitle = detailMap[s.code]?.title?.trim();
            // Si en Firestore quedó guardado el título antiguo muy largo del 08, usamos la versión concisa en la tarjeta
            const displayTitle =
              s.code === "08" && customTitle && customTitle.length > 48
                ? s.title
                : customTitle || s.title;

            return (
              <Fragment key={s.code}>
                <li
                  className={styles.card}
                  data-active={isActive}
                  data-collapsed={collapsed}
                  onClick={() => setSelected(isActive ? null : s.code)}
                >
                  <div className={styles.svgWrap}>
                    <s.Icon size={80} />
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.code}>{s.code}</span>
                    <h3 className={styles.title}>{displayTitle}</h3>
                    <p className={styles.body}>{s.body}</p>
                  </div>
                  <button
                    type="button"
                    className={styles.detailsBtn}
                    data-active={isActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(isActive ? null : s.code);
                    }}
                    aria-expanded={isActive}
                    aria-label={isActive ? "Contraer detalles" : "Ver detalles"}
                  >
                    <span className={styles.detailsLabel}>
                      {isActive ? "Cerrar" : "Detalles"}
                    </span>
                    <svg className={styles.chev} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                      <path
                        d="M4 6 L8 10 L12 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </li>

                {/* En móvil el panel se inserta justo debajo de la tarjeta activa */}
                {isMobile && isActive && open && (
                  <li className={styles.panelItem}>
                    <ServiceDetailPanel
                      service={open}
                      detail={detailMap[open.code]}
                      onClose={() => setSelected(null)}
                      inline
                    />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ul>

        {/* En desktop el panel ocupa la mitad derecha */}
        {!isMobile && open && (
          <ServiceDetailPanel
            service={open}
            detail={detailMap[open.code]}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </section>
  );
}

function ServiceDetailPanel({ service, detail, onClose, inline = false }) {
  const { items: images } = useFirestoreCollection(
    `services/${service.code}/images`,
    orderBy("order")
  );
  const html = detail?.detail?.trim();

  return (
    <aside className={`${styles.panel} ${inline ? styles.panelInline : ""}`}>
      <div className={styles.panelHead}>
        <div>
          <span className={styles.code}>// frente {service.code}</span>
          <h3 className={styles.panelTitle}>
            {detail?.title?.trim() || service.title}
          </h3>
        </div>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Cerrar detalles"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6 6 18 M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className={styles.svgWrapPanel}>
        <service.Icon size={72} />
      </div>

      {html ? (
        <div
          className={styles.panelText}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p className={styles.panelText}>{service.body}</p>
      )}

      {images.length > 0 && (
        <div className={styles.gallery}>
          {images.map((im) => (
            <img key={im.id} src={im.url} alt={im.alt || service.title} loading="lazy" />
          ))}
        </div>
      )}

      <div className={styles.panelFoot}>
        <Link to="/contacto" className={styles.panelCta}>
          Consultar por este servicio
          <svg viewBox="0 0 24 12" width="18" height="9" aria-hidden="true">
            <path d="M0 6 H22 M16 1 L22 6 L16 11" fill="none" stroke="currentColor" strokeWidth="1.4" />
          </svg>
        </Link>
      </div>
    </aside>
  );
}
